import { useCallback, useEffect, useRef, useState } from "react";
import wsClient from "@/lib/websocket";

// One refetch per hook call per window, not per key: two events about one
// record cost one refetch, and a bulk run that moves five rows on one list
// page costs one refetch rather than five. Safe because a refetch always
// reads current state.
const DEBOUNCE_MS = 200;

// How often a screen refetches by itself while the socket is down.
//
// `websocket.js` gives up after five failed reconnects and waits for the tab
// to refocus or the network to return. Nothing else refetches: change fires
// on an event, an open or a resync, and none of the three can happen while
// the socket is gone. So a mounted list sat there looking live and receiving
// nothing, for as long as the tab stayed in front of the person reading it
// (I1768 W6).
//
// A minute, not a second: this is the fallback for a socket that is already
// gone, and the reconnect path is what recovers the ordinary case. Slow
// enough that a tenant behind a broken proxy costs the API one request per
// screen per minute, and quick enough that nobody reads a stale board for
// long.
const OFFLINE_POLL_MS = 60000;

/**
 * Subscribe a screen to a resource, and refetch when it changes.
 *
 * ```jsx
 * const { stale } = useLiveResource({
 *   keys: ["job:" + jobId, "application"],
 *   match: (event) => event.resource !== "application" || event.parent?.job === jobId,
 *   onChange: () => fetchJob({ silent: true }),
 * });
 * ```
 *
 * @param {string|string[]} keys  subscription keys, sent in the subscribe frame
 * @param {(event) => boolean} [match]  narrows on `id` / `parent`; a subscription
 *   key is a resource namespace, not a query, so a page that cares about one
 *   parent's children subscribes to the child resource and narrows here
 * @param {(event) => void} [onEvent]  synchronous and undebounced, for the
 *   status and the counts no refetch would show
 * @param {() => void} [onChange]  debounced {@link DEBOUNCE_MS}; also fires on
 *   a reconnect and on a detected sequence gap
 * @param {boolean} [enabled=true]
 * @param {"auth"|"public"|"link"} [transport="auth"]  a public board's socket, for a
 *   visitor with no session
 * @param {string} [slug]  the tenant slug, for the public transport
 * @param {string} [token]  the magic-link token, for the link transport
 * @returns {{stale: boolean}} true while the socket is down. The hook acts on
 *   it itself — see {@link OFFLINE_POLL_MS} — so a screen need not read it
 *
 * Two deliberate narrowings of the design's §4.1:
 *
 * - the debounce is per hook call, not per key (see {@link DEBOUNCE_MS});
 * - there is no refetch on mount. Every screen already fetches on mount with
 *   its own page, filter and search params; a second fetch from here would be
 *   a regression on all of them. That mount effect *is* the mount fetch.
 */
const useLiveResource = ({ keys, match, onEvent, onChange, enabled = true, transport = "auth", slug, token } = {}) => {
  const keyList = Array.isArray(keys) ? keys : keys ? [keys] : [];
  // Screens pass inline closures; without refs, every render would resubscribe.
  const matchRef = useRef(match);
  const onEventRef = useRef(onEvent);
  const onChangeRef = useRef(onChange);
  matchRef.current = match;
  onEventRef.current = onEvent;
  onChangeRef.current = onChange;

  const [stale, setStale] = useState(() => enabled && !wsClient.isConnected);

  const timerRef = useRef(null);
  const scheduleChange = useCallback(() => {
    if (!onChangeRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onChangeRef.current?.();
    }, DEBOUNCE_MS);
  }, []);

  // The keys are a value, not an identity: a screen rebuilding the array every
  // render must not resubscribe every render.
  const keySignature = keyList.join("|");

  useEffect(() => {
    if (!enabled) return undefined;

    const subscribed = keySignature ? keySignature.split("|") : [];
    wsClient.ensureConnected({ transport, slug, token });
    if (subscribed.length) wsClient.subscribe(subscribed);

    const stop = wsClient.listen((envelope) => {
      const name = envelope?.event;
      if (!name) return;

      if (name === "__open__") {
        setStale(false);
        // A reconnect and a gap are the same story: something may have
        // happened while this client was not listening.
        scheduleChange();
        return;
      }
      if (name === "__resync__") {
        scheduleChange();
        return;
      }
      if (name === "__close__") {
        setStale(true);
        return;
      }

      // The server only delivers what this connection subscribed to, plus the
      // four unfiltered events. Narrow to this hook's own keys anyway — every
      // hook in the tab hears every frame.
      if (!subscribed.some((key) => key === envelope.resource || key === `${envelope.resource}:${envelope.id}`)) return;
      if (matchRef.current && !matchRef.current(envelope)) return;

      onEventRef.current?.(envelope);
      scheduleChange();
    });

    return () => {
      stop();
      clearTimeout(timerRef.current);
      timerRef.current = null;
      if (subscribed.length) wsClient.unsubscribe(subscribed);
    };
  }, [enabled, keySignature, transport, slug, token, scheduleChange]);

  // The socket is down and the client may have stopped trying. Refetch on a
  // slow timer until it is back, so the screen is behind by at most
  // {@link OFFLINE_POLL_MS} rather than behind forever (I1768 W6). Nothing is
  // rendered for it: a screen that quietly keeps itself current is the whole
  // of the fix.
  useEffect(() => {
    if (!enabled || !stale) return undefined;

    const timer = setInterval(() => scheduleChange(), OFFLINE_POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, stale, scheduleChange]);

  return { stale };
};

export default useLiveResource;
